import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Check, X, Camera, CaretDown, CaretUp, Plus, Trash } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BirthdayDecoratorSelector = ({ onSave }) => {
  const [themes, setThemes] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedThemes, setSelectedThemes] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const fileInputRef = useRef(null);

  // Custom theme state
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [customPhoto, setCustomPhoto] = useState(null);
  const [customUploading, setCustomUploading] = useState(false);
  const customFileRef = useRef(null);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/quick-add-items?vendor_category=birthday_decorator`);
      setThemes(data.filter(i => i.item_type === 'birthday_theme'));
      setAddons(data.filter(i => i.item_type === 'birthday_addon'));
    } catch { /* fallback empty */ }
    setLoaded(true);
  };

  const toggleTheme = (theme) => {
    const exists = selectedThemes.find(t => t.name === theme.name);
    if (exists) {
      setSelectedThemes(selectedThemes.filter(t => t.name !== theme.name));
    } else {
      setSelectedThemes([...selectedThemes, {
        name: theme.name,
        price: theme.defaultPrice,
        photo: null,
        expanded: true,
        addons: [],
        custom: !!theme.custom,
      }]);
    }
  };

  const addCustomTheme = () => {
    const name = customName.trim();
    const price = parseInt(customPrice) || 0;
    if (!name) { toast.error('Enter a theme name'); return; }
    if (!price) { toast.error('Enter a base price'); return; }
    if (!customPhoto) { toast.error('Upload at least 1 image for the custom theme'); return; }
    const exists = selectedThemes.find(t => t.name.toLowerCase() === name.toLowerCase()) ||
                   themes.find(t => t.name.toLowerCase() === name.toLowerCase());
    if (exists) { toast.error('Theme name already exists'); return; }
    setSelectedThemes([...selectedThemes, {
      name, price, photo: customPhoto, expanded: true, addons: [], custom: true,
    }]);
    setCustomName('');
    setCustomPrice('');
    setCustomPhoto(null);
    setShowCustomForm(false);
    toast.success(`Custom theme "${name}" added — configure add-ons below`);
  };

  const handleCustomPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCustomUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${BACKEND_URL}/api/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setCustomPhoto(resp.data.url);
      toast.success('Image uploaded!');
    } catch {
      toast.error('Upload failed');
    }
    setCustomUploading(false);
    if (customFileRef.current) customFileRef.current.value = '';
  };

  const removeCustomTheme = (name) => {
    setSelectedThemes(selectedThemes.filter(t => t.name !== name));
  };

  const toggleExpand = (idx) => {
    const u = [...selectedThemes];
    u[idx].expanded = !u[idx].expanded;
    setSelectedThemes(u);
  };

  const calcThemeTotal = (t) => {
    let total = parseInt(t.price) || 0;
    (t.addons || []).forEach(a => { total += parseInt(a.price) || 0; });
    return total;
  };

  const toggleAddon = (tIdx, addon) => {
    const u = [...selectedThemes];
    const t = u[tIdx];
    const exists = t.addons.find(a => a.name === addon.name);
    if (exists) {
      t.addons = t.addons.filter(a => a.name !== addon.name);
    } else {
      t.addons.push({ name: addon.name, category: addon.category, price: addon.defaultPrice, photo: null });
    }
    setSelectedThemes(u);
  };

  const updateAddonPrice = (tIdx, aIdx, price) => {
    const u = [...selectedThemes];
    u[tIdx].addons[aIdx].price = parseInt(price) || 0;
    setSelectedThemes(u);
  };

  const updateThemePrice = (tIdx, price) => {
    const u = [...selectedThemes];
    u[tIdx].price = parseInt(price) || 0;
    setSelectedThemes(u);
  };

  // Photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !uploadTarget) return;
    setUploading(uploadTarget.key);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${BACKEND_URL}/api/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const url = resp.data.url;
      const { type, tIdx, aIdx } = uploadTarget;
      const updated = [...selectedThemes];
      if (type === 'theme') updated[tIdx].photo = url;
      else if (type === 'addon') updated[tIdx].addons[aIdx].photo = url;
      setSelectedThemes(updated);
      toast.success('Photo uploaded!');
    } catch {
      toast.error('Upload failed');
    }
    setUploading(null);
    setUploadTarget(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const triggerUpload = (target) => {
    setUploadTarget(target);
    setTimeout(() => fileInputRef.current?.click(), 50);
  };

  const handleSaveAll = () => {
    if (selectedThemes.length === 0) { toast.error('Select at least one theme'); return; }
    const customMissing = selectedThemes.filter(t => t.custom && !t.photo);
    if (customMissing.length > 0) {
      toast.error(`Upload an image for custom theme: ${customMissing.map(t => t.name).join(', ')}`);
      return;
    }
    onSave({ themes: selectedThemes });
  };

  const addonCategories = [...new Set(addons.map(a => a.category))];

  const PhotoBox = ({ photoUrl, uploadKey, onTrigger, size = 'md' }) => {
    const sizeClass = size === 'lg' ? 'w-20 h-20' : 'w-12 h-12';
    return (
      <div
        className={`${sizeClass} border border-dashed border-[#E8E1D5] rounded flex-shrink-0 flex items-center justify-center cursor-pointer overflow-hidden bg-white hover:border-brand-primary transition-colors`}
        onClick={onTrigger}
        data-testid={`bd-photo-box-${uploadKey}`}
      >
        {uploading === uploadKey ? (
          <span className="text-xs text-text-secondary">...</span>
        ) : photoUrl ? (
          <img src={`${BACKEND_URL}${photoUrl}`} alt="" className="w-full h-full object-cover" />
        ) : (
          <Camera size={size === 'lg' ? 24 : 18} className="text-text-secondary" />
        )}
      </div>
    );
  };

  if (!loaded) return <div className="text-center py-8 text-text-secondary">Loading items...</div>;

  return (
    <div className="space-y-3">
      <input type="file" accept="image/*" ref={fileInputRef} onChange={handlePhotoUpload} className="hidden" />
      <p className="text-sm text-text-secondary">Select birthday themes you offer. Add decoration add-ons with photos and prices for each theme.</p>

      {/* Custom Theme Add */}
      <div className="border-2 border-dashed border-pink-300 rounded-md bg-pink-50/40 p-3">
        {!showCustomForm ? (
          <Button
            type="button"
            onClick={() => setShowCustomForm(true)}
            className="w-full bg-white border border-pink-400 text-pink-700 hover:bg-pink-100 rounded-none"
            data-testid="add-custom-theme-btn"
          >
            <Plus size={16} className="mr-1" /> Add Custom Theme
          </Button>
        ) : (
          <div className="space-y-2">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">New Custom Theme</div>
            <div className="flex flex-col md:flex-row gap-2 items-start">
              {/* Mandatory image upload */}
              <input
                type="file"
                accept="image/*"
                ref={customFileRef}
                onChange={handleCustomPhotoUpload}
                className="hidden"
              />
              <div
                className={`w-20 h-20 border-2 border-dashed rounded flex-shrink-0 flex items-center justify-center cursor-pointer overflow-hidden bg-white transition-colors ${customPhoto ? 'border-pink-400' : 'border-red-400'}`}
                onClick={() => customFileRef.current?.click()}
                data-testid="custom-theme-photo-box"
                title={customPhoto ? 'Replace image' : 'Upload image (required)'}
              >
                {customUploading ? (
                  <span className="text-xs text-text-secondary">...</span>
                ) : customPhoto ? (
                  <img src={`${BACKEND_URL}${customPhoto}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center gap-0.5">
                    <Camera size={20} className="text-red-500" />
                    <span className="text-[9px] font-bold text-red-600 uppercase tracking-wider">Required</span>
                  </div>
                )}
              </div>
              <div className="flex-1 flex flex-col md:flex-row gap-2 w-full">
                <Input
                  placeholder="Theme name (e.g. Pirate Theme)"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  className="rounded-none border-[#E8E1D5] flex-1 h-9 text-sm"
                  data-testid="custom-theme-name-input"
                />
                <div className="flex items-center gap-1">
                  <span className="text-sm text-brand-primary">₹</span>
                  <Input
                    type="number"
                    placeholder="Base price"
                    value={customPrice}
                    onChange={(e) => setCustomPrice(e.target.value)}
                    className="rounded-none border-[#E8E1D5] w-32 h-9 text-sm"
                    data-testid="custom-theme-price-input"
                  />
                </div>
                <Button
                  type="button"
                  onClick={addCustomTheme}
                  disabled={!customName.trim() || !customPrice || !customPhoto}
                  className="bg-pink-600 hover:bg-pink-700 text-white rounded-none disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="custom-theme-save-btn"
                >
                  <Check size={14} className="mr-1" /> Add
                </Button>
                <Button
                  type="button"
                  onClick={() => { setShowCustomForm(false); setCustomName(''); setCustomPrice(''); setCustomPhoto(null); }}
                  variant="ghost"
                  size="sm"
                >
                  <X size={14} />
                </Button>
              </div>
            </div>
            <p className="text-xs text-text-secondary">
              <span className="text-red-600 font-semibold">Image upload is required.</span> Click the photo box to upload your theme image, then enter name & base price.
            </p>
          </div>
        )}
      </div>

      {/* Custom themes (already added by vendor) appear first */}
      {selectedThemes.filter(t => t.custom).map((selected) => {
        const tIdx = selectedThemes.findIndex(t => t.name === selected.name);
        return (
          <div key={`custom-${selected.name}`} className="border-2 border-pink-500 bg-pink-50/30 rounded-md transition-all">
            <div className="flex items-center gap-3 p-3" data-testid={`custom-theme-${selected.name.toLowerCase().replace(/ /g, '-')}`}>
              <span className="rounded-full w-10 h-10 bg-pink-600 text-white flex items-center justify-center flex-shrink-0">
                <Check size={18} weight="bold" />
              </span>
              <PhotoBox photoUrl={selected.photo} uploadKey={`theme-${selected.name}`}
                onTrigger={() => triggerUpload({ key: `theme-${selected.name}`, type: 'theme', tIdx })}
                size="lg" />
              <div className="flex-1 font-medium cursor-pointer" onClick={() => toggleExpand(tIdx)}>
                <span className="inline-flex items-center gap-1">
                  <span className="text-[10px] px-1.5 py-0.5 bg-pink-600 text-white rounded font-bold uppercase tracking-wider">Custom</span>
                  {selected.name}
                </span>
                <span className="text-xs text-text-secondary ml-2">({selected.addons.length} add-ons)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-brand-primary">
                  ₹{calcThemeTotal(selected).toLocaleString('en-IN')}
                </span>
                <Button type="button" variant="ghost" size="sm" onClick={() => toggleExpand(tIdx)}>
                  {selected.expanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeCustomTheme(selected.name)}
                  className="text-red-500 hover:text-red-700" data-testid={`remove-custom-${selected.name.toLowerCase().replace(/ /g, '-')}`}>
                  <Trash size={14} />
                </Button>
              </div>
            </div>

            {selected.expanded && (
              <div className="border-t border-[#E8E1D5] px-4 py-3 space-y-4 bg-white/50">
                <div className="flex items-center gap-3">
                  <span className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Theme Base Price</span>
                  <div className="flex items-center">
                    <span className="text-sm text-brand-primary mr-1">₹</span>
                    <Input
                      type="number"
                      value={selected.price}
                      onChange={(e) => updateThemePrice(tIdx, e.target.value)}
                      className="rounded-none border-[#E8E1D5] w-32 h-8 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2">
                    Decoration Add-ons for {selected.name}
                  </div>
                  {addonCategories.map(cat => {
                    const catItems = addons.filter(a => a.category === cat);
                    if (catItems.length === 0) return null;
                    const catColors = {
                      'Balloons': 'bg-pink-500',
                      'Backdrops': 'bg-purple-600',
                      'Lighting': 'bg-amber-500',
                      'Props': 'bg-blue-600',
                      'Table & Stage': 'bg-green-600',
                    };
                    return (
                      <div key={cat} className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs px-2 py-0.5 text-white font-bold uppercase tracking-wider ${catColors[cat] || 'bg-gray-600'}`}>{cat}</span>
                          <span className="text-xs text-text-secondary">{catItems.length} items</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {catItems.map((addon, aGlobalIdx) => {
                            const aSelected = selected.addons.find(a => a.name === addon.name);
                            const aIdx = selected.addons.findIndex(a => a.name === addon.name);
                            return (
                              <div key={aGlobalIdx} className={`flex items-center gap-2 p-2 border rounded transition-all ${aSelected ? 'border-pink-400 bg-pink-50' : 'border-[#E8E1D5]'}`}>
                                <Button type="button" onClick={() => toggleAddon(tIdx, addon)}
                                  className={`rounded-full w-8 h-8 p-0 flex-shrink-0 ${aSelected ? 'bg-pink-600 hover:bg-pink-700' : 'bg-red-500 hover:bg-red-600'} text-white`}>
                                  {aSelected ? <Check size={14} weight="bold" /> : <X size={14} weight="bold" />}
                                </Button>
                                {aSelected && (
                                  <PhotoBox photoUrl={aSelected.photo} uploadKey={`addon-${tIdx}-${addon.name}`}
                                    onTrigger={() => triggerUpload({ key: `addon-${tIdx}-${addon.name}`, type: 'addon', tIdx, aIdx })} />
                                )}
                                <span className="flex-1 text-sm">{addon.name}</span>
                                {aSelected ? (
                                  <Input type="number" value={aSelected.price}
                                    onChange={(e) => updateAddonPrice(tIdx, aIdx, e.target.value)}
                                    className="rounded-none border-[#E8E1D5] w-24 h-7 text-xs" />
                                ) : (
                                  <span className="text-xs font-semibold text-brand-primary">₹{addon.defaultPrice.toLocaleString('en-IN')}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {themes.map((theme, idx) => {
        const selected = selectedThemes.find(t => t.name === theme.name);
        const tIdx = selectedThemes.findIndex(t => t.name === theme.name);

        return (
          <div key={idx} className={`border-2 rounded-md transition-all ${selected ? 'border-pink-500 bg-pink-50/30' : 'border-[#E8E1D5] bg-white'}`}>
            {/* Theme header */}
            <div className="flex items-center gap-3 p-3" data-testid={`toggle-theme-${theme.name.toLowerCase().replace(/ /g, '-')}`}>
              <Button
                type="button" onClick={() => toggleTheme(theme)}
                className={`rounded-full w-10 h-10 p-0 flex-shrink-0 ${selected ? 'bg-pink-600 hover:bg-pink-700' : 'bg-red-500 hover:bg-red-600'} text-white`}
              >
                {selected ? <Check size={18} weight="bold" /> : <X size={18} weight="bold" />}
              </Button>
              {selected && (
                <PhotoBox photoUrl={selected.photo} uploadKey={`theme-${theme.name}`}
                  onTrigger={() => triggerUpload({ key: `theme-${theme.name}`, type: 'theme', tIdx })}
                  size="lg" />
              )}
              <div className="flex-1 font-medium cursor-pointer" onClick={() => selected && toggleExpand(tIdx)}>
                {theme.name}
                {selected && <span className="text-xs text-text-secondary ml-2">({selected.addons.length} add-ons)</span>}
              </div>
              {selected ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-brand-primary" data-testid={`theme-total-${theme.name.toLowerCase().replace(/ /g, '-')}`}>
                    ₹{calcThemeTotal(selected).toLocaleString('en-IN')}
                  </span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => toggleExpand(tIdx)}>
                    {selected.expanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
                  </Button>
                </div>
              ) : (
                <span className="text-sm font-semibold text-brand-primary">₹{theme.defaultPrice.toLocaleString('en-IN')}</span>
              )}
            </div>

            {/* Expanded content - theme price + add-ons */}
            {selected && selected.expanded && (
              <div className="border-t border-[#E8E1D5] px-4 py-3 space-y-4 bg-white/50">
                {/* Editable theme base price */}
                <div className="flex items-center gap-3">
                  <span className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">Theme Base Price</span>
                  <div className="flex items-center">
                    <span className="text-sm text-brand-primary mr-1">₹</span>
                    <Input
                      type="number"
                      value={selected.price}
                      onChange={(e) => updateThemePrice(tIdx, e.target.value)}
                      className="rounded-none border-[#E8E1D5] w-32 h-8 text-sm"
                      data-testid={`theme-price-${theme.name.toLowerCase().replace(/ /g, '-')}`}
                    />
                  </div>
                </div>

                {/* Decoration add-ons by category */}
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2">
                    Decoration Add-ons for {theme.name}
                  </div>
                  {addonCategories.map(cat => {
                    const catItems = addons.filter(a => a.category === cat);
                    if (catItems.length === 0) return null;
                    const catColors = {
                      'Balloons': 'bg-pink-500',
                      'Backdrops': 'bg-purple-600',
                      'Lighting': 'bg-amber-500',
                      'Props': 'bg-blue-600',
                      'Table & Stage': 'bg-green-600',
                    };
                    return (
                      <div key={cat} className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs px-2 py-0.5 text-white font-bold uppercase tracking-wider ${catColors[cat] || 'bg-gray-600'}`}>{cat}</span>
                          <span className="text-xs text-text-secondary">{catItems.length} items</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {catItems.map((addon, aGlobalIdx) => {
                            const aSelected = selected.addons.find(a => a.name === addon.name);
                            const aIdx = selected.addons.findIndex(a => a.name === addon.name);
                            return (
                              <div key={aGlobalIdx} className={`flex items-center gap-2 p-2 border rounded transition-all ${aSelected ? 'border-pink-400 bg-pink-50' : 'border-[#E8E1D5]'}`}>
                                <Button type="button" onClick={() => toggleAddon(tIdx, addon)}
                                  className={`rounded-full w-8 h-8 p-0 flex-shrink-0 ${aSelected ? 'bg-pink-600 hover:bg-pink-700' : 'bg-red-500 hover:bg-red-600'} text-white`}>
                                  {aSelected ? <Check size={14} weight="bold" /> : <X size={14} weight="bold" />}
                                </Button>
                                {aSelected && (
                                  <PhotoBox photoUrl={aSelected.photo} uploadKey={`addon-${tIdx}-${addon.name}`}
                                    onTrigger={() => triggerUpload({ key: `addon-${tIdx}-${addon.name}`, type: 'addon', tIdx, aIdx })} />
                                )}
                                <span className="flex-1 text-sm">{addon.name}</span>
                                {aSelected ? (
                                  <Input type="number" value={aSelected.price}
                                    onChange={(e) => updateAddonPrice(tIdx, aIdx, e.target.value)}
                                    className="rounded-none border-[#E8E1D5] w-24 h-7 text-xs" />
                                ) : (
                                  <span className="text-xs font-semibold text-brand-primary">₹{addon.defaultPrice.toLocaleString('en-IN')}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div className="sticky bottom-4 bg-sand-50 p-4 border-t border-[#E8E1D5]">
        <Button type="button" onClick={handleSaveAll} disabled={selectedThemes.length === 0}
          className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-none" data-testid="save-birthday-decorator-btn">
          Save {selectedThemes.length} Theme{selectedThemes.length !== 1 ? 's' : ''}
        </Button>
      </div>
    </div>
  );
};

export default BirthdayDecoratorSelector;
